import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useConfirm } from '@/providers/ConfirmProvider';

// Cores funcionais das alternativas (estilo Kahoot) — não temas de marca,
// é correspondência visual com o app do aluno.
const ALTERNATIVE_COLORS = ['bg-danger', 'bg-primary', 'bg-warning', 'bg-success'];

export default function QuestionView({ challenge, challengeId, onPhaseChange }) {
  const { currentQuestion, timeRemaining, timerSeconds, isTimerRunning, answerCount, questionIndex, totalQuestions, startTimer } = useLiveChallenge();
  const { confirm } = useConfirm();
  const [localQuestion, setLocalQuestion] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [localTimer, setLocalTimer] = useState(0);
  const [localTimerMax, setLocalTimerMax] = useState(30);

  const activeQuestion = currentQuestion || localQuestion;
  const alternatives = activeQuestion?.['live-challenge-alternatives'] || activeQuestion?.live_challenge_alternatives || activeQuestion?.alternatives || [];
  const activeTimer = timeRemaining || localTimer;
  const activeTimerMax = timerSeconds || localTimerMax;
  const progress = activeTimerMax > 0 ? (activeTimer / activeTimerMax) * 100 : 0;
  const answeredRatio = answerCount.total > 0 ? Math.round((answerCount.answered / answerCount.total) * 100) : 0;

  const handleRevealQuestion = async () => {
    setRevealing(true);
    try {
      const response = await liveChallengesApi.revealQuestion(challengeId);
      const raw = response?.data;
      const question = raw?.attributes ? { id: raw.id, ...Object.fromEntries(Object.entries(raw.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v])) } : raw;
      setLocalQuestion(question);
      const timer = question?.timer_seconds || 30;
      setLocalTimerMax(timer);
      setLocalTimer(timer);
      startTimer(timer);
    } catch {
      toast.error('Erro ao revelar questão.');
    } finally {
      setRevealing(false);
    }
  };

  const handleCloseQuestion = async () => {
    try {
      await liveChallengesApi.closeQuestion(challengeId);
      if (onPhaseChange) onPhaseChange('questionResult');
    } catch {
      toast.error('Erro ao encerrar questão.');
    }
  };

  if (!activeQuestion) {
    const allQuestions = challenge?.live_challenge_questions || [];
    const currentIdx = challenge?.current_question_index || 0;
    const nextQuestion = allQuestions[currentIdx];
    const upcoming = allQuestions.slice(currentIdx + 1);
    const handleBackToLobby = async () => {
      const ok = await confirm({
        title: 'Voltar para o lobby?',
        description: 'O desafio será reiniciado do zero. Todos os participantes serão removidos e qualquer pontuação acumulada será perdida.',
        confirmText: 'Sim, recomeçar',
        cancelText: 'Cancelar',
        variant: 'destructive'
      });
      if (!ok) return;

      try {
        await liveChallengesApi.backToLobby(challengeId);
        toast.success('Desafio reiniciado. Aguardando participantes no lobby.');
        if (onPhaseChange) onPhaseChange('lobby');
      } catch (err) {
        const message = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao voltar para o lobby.';
        toast.error(message);
      }
    };

    return (
      <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
        {/* Coluna 1: preview da próxima questão */}
        <div className="flex flex-col gap-4">
          <header>
            <h2 className="m-0 text-xl font-bold text-foreground">Próxima questão</h2>
            <p className="mt-1 text-sm text-neutral">Revise antes de revelar aos participantes.</p>
          </header>

          {nextQuestion ? (
            <Card
              border
              orientation="vertical"
              className="px-5"
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Questão {currentIdx + 1} de {allQuestions.length}
                </span>
              </div>
              <p className="mb-4 text-base font-semibold text-neutral-dark">{nextQuestion.description}</p>
              <ul className="m-0 flex list-none flex-col gap-2 pl-0">
                {(nextQuestion.live_challenge_alternatives || []).map((alt, idx) => (
                  <li
                    key={alt.id || idx}
                    className={`flex items-start gap-2 rounded-md border border-solid px-3 py-2 text-sm ${alt.is_correct ? 'border-success/40 bg-success-lightest text-success' : 'border-neutral-lightest text-neutral-dark'}`}
                  >
                    <span className="font-bold">{String.fromCharCode(65 + idx)}.</span>
                    <span>{alt.description}</span>
                    {alt.is_correct && <span className="ml-auto text-xs font-semibold uppercase tracking-wide">correta</span>}
                  </li>
                ))}
              </ul>
            </Card>
          ) : (
            <p className="m-0 text-sm text-neutral-light">Nenhuma questão configurada.</p>
          )}
        </div>

        {/* Coluna 2: ações + lista de subsequentes */}
        <div className="flex flex-col gap-6">
          <Card
            border
            orientation="vertical"
            className="px-5"
          >
            <h3 className="m-0 mb-2 text-base font-semibold text-foreground">Desafio iniciado</h3>
            <p className="mb-4 text-sm text-neutral">Quando estiver pronto, libere a questão acima para os participantes responderem.</p>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="default"
                size="lg"
                fullWidth
                disabled={revealing || !nextQuestion}
                onClick={handleRevealQuestion}
              >
                {revealing ? 'Revelando...' : 'Revelar questão'}
              </Button>
              <Button
                type="button"
                variant="outline-danger"
                size="md"
                fullWidth
                onClick={handleBackToLobby}
              >
                Reiniciar e voltar para o lobby
              </Button>
            </div>
          </Card>

          {upcoming.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3 className="m-0 text-base font-semibold text-foreground">Próximas questões</h3>
              <Card
                border
                orientation="vertical"
                className="px-0 py-0"
              >
                <ol className="m-0 list-none pl-0">
                  {upcoming.map((q, idx) => (
                    <li
                      key={q.id || idx}
                      className="flex items-center gap-3 border-b border-solid border-neutral-lightest px-4 py-3 text-sm last:border-0"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-lightest text-xs font-bold text-primary-dark">{currentIdx + idx + 2}</span>
                      <span className="line-clamp-2 text-neutral-dark">{q.description || `Questão ${currentIdx + idx + 2}`}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isUrgent = activeTimer <= 5;

  return (
    <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
      {/* Coluna 1: Questão e alternativas (o que o aluno vê) */}
      <div className="flex flex-col gap-6">
        <p className="m-0 text-sm font-semibold text-neutral">
          Questão {(questionIndex || 0) + 1} de {totalQuestions || '?'}
        </p>

        <Card
          border
          orientation="vertical"
          className="px-6 py-6 text-center"
        >
          <h2 className="m-0 text-xl font-bold text-foreground">{activeQuestion?.description || 'Carregando questão...'}</h2>
        </Card>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {alternatives.map((alt, idx) => (
            <div
              key={alt.id || idx}
              className={`flex items-center justify-center rounded-lg p-5 text-center text-base font-bold text-white shadow-dropshadow-md ${ALTERNATIVE_COLORS[idx % ALTERNATIVE_COLORS.length]}`}
            >
              <span className="mr-2 text-lg opacity-80">{String.fromCharCode(65 + idx)}</span>
              {alt.description}
            </div>
          ))}
        </div>
      </div>

      {/* Coluna 2: Timer + progresso + ação (controle do professor) */}
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-2">
          <div className="relative flex h-32 w-32 items-center justify-center">
            <svg
              className="h-32 w-32 -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-neutral-lightest"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className={`transition-all duration-1000 ease-linear ${isUrgent ? 'text-danger' : 'text-primary'}`}
              />
            </svg>
            <span className="absolute text-3xl font-bold text-foreground">{activeTimer}</span>
          </div>
          {isTimerRunning && <p className="m-0 text-xs text-neutral-light">segundos restantes</p>}
        </div>

        <Card
          border
          orientation="vertical"
          className="w-full px-5"
        >
          <div className="mb-2 flex justify-between text-sm text-neutral-dark">
            <span className="font-semibold">Respostas recebidas</span>
            <span className="font-bold text-primary">{answeredRatio}%</span>
          </div>
          <div className="mb-2 text-xs text-neutral">
            {answerCount.answered} de {answerCount.total} participantes
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-neutral-lightest">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out-strong"
              style={{ width: `${answeredRatio}%` }}
            />
          </div>
        </Card>

        <Button
          type="button"
          variant="destructive"
          size="lg"
          fullWidth
          onClick={handleCloseQuestion}
        >
          Encerrar Questão
        </Button>
      </div>
    </div>
  );
}
